import { Question } from '../_models/question';

/** Calculated dependency status is always kept. Matches QuestionsService.addDependencyQuestion. */
const DEPENDENCY_STATUS_ID = 'student.dependencyStatus';

export interface MinimumQuestionsResult {
  questions: Question[];
  added: string[];
  removed: string[];
}

export function applyMinimumMethodologyQuestions(
  draft: Question[],
  allQuestions: Question[],
  institutionalMethod: boolean,
  usedQuestionIds: ReadonlySet<string>,
): MinimumQuestionsResult {
  const masterById = new Map(allQuestions.map(question => [question.id, question]));
  const draftById = new Map(draft.map(question => [question.id, question]));
  const keepIds = new Set<string>();

  for (const question of draft) {
    if (shouldSeed(question, masterById.get(question.id), institutionalMethod, usedQuestionIds)) {
      keepIds.add(question.id);
    }
  }
  for (const question of allQuestions) {
    if (methodologyRequired(question, institutionalMethod)) {
      keepIds.add(question.id);
    }
  }

  const catalog = [...draft, ...allQuestions];
  let expanded = true;
  while (expanded) {
    expanded = false;
    for (const question of catalog) {
      if (!question.id) {
        continue;
      }
      const parentId = question.triggerQuestionId;
      if (!question.dateDeleted && parentId && keepIds.has(parentId) && !keepIds.has(question.id)) {
        keepIds.add(question.id);
        expanded = true;
      }
      if (keepIds.has(question.id) && parentId && !keepIds.has(parentId)) {
        const parent = draftById.get(parentId) ?? masterById.get(parentId);
        if (parent && !parent.dateDeleted) {
          keepIds.add(parentId);
          expanded = true;
        }
      }
    }
  }

  const questions: Question[] = [];
  for (const id of keepIds) {
    const existing = draftById.get(id);
    const master = masterById.get(id);
    const methodRequired = id === DEPENDENCY_STATUS_ID || methodologyRequired(master, institutionalMethod);

    if (existing && !existing.dateDeleted) {
      questions.push(copyQuestion(existing, methodRequired));
    } else if (master && !master.dateDeleted) {
      questions.push(copyQuestion(master, methodRequired));
    } else if (existing && (usedQuestionIds.has(id) || id === DEPENDENCY_STATUS_ID)) {
      questions.push(copyQuestion(existing, methodRequired));
    }
  }

  questions.sort((a, b) => a.ordinal - b.ordinal);

  const draftIds = new Set(draft.map(question => question.id));
  const resultIds = new Set(questions.map(question => question.id));
  return {
    questions,
    added: questions.filter(question => !draftIds.has(question.id)).map(question => question.id),
    removed: draft.filter(question => !resultIds.has(question.id)).map(question => question.id),
  };
}

function shouldSeed(
  question: Question,
  master: Question | undefined,
  institutionalMethod: boolean,
  usedQuestionIds: ReadonlySet<string>,
): boolean {
  return methodologyRequired(master ?? question, institutionalMethod)
    || usedQuestionIds.has(question.id)
    || question.id === DEPENDENCY_STATUS_ID;
}

function methodologyRequired(question: Question | undefined, institutionalMethod: boolean): boolean {
  if (!question || question.dateDeleted) {
    return false;
  }
  return institutionalMethod ? question.imRequired === true : question.fmRequired === true;
}

function copyQuestion(question: Question, methodRequired: boolean): Question {
  return {
    ...question,
    methodRequired,
    choices: question.choices.map(choice => ({ ...choice })),
  };
}
